/**
 * The Solution context bar on the plan header (RP-15.1).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 * ──────────────────────────────────────────────────────────────────────────────
 * Pricing a plan is a series of judgements about a Solution — is this Intensity right,
 * does the mix suit an ICP we have actually staffed before, is the tier the one the rates
 * were quoted against — and every one of those answers lived on a different screen. The
 * planner's loop was: read a number here, leave, remember, come back. This puts the
 * Solution's own attributes above the grid so the loop closes.
 *
 * It is DISPLAY ONLY, and deliberately so. Every field here is owned by Solution Detail,
 * which is where Constraints and ratings are edited; duplicating those controls would
 * give the same value two masters. The bar opens Solution Detail instead, and that button
 * is the point of the component as much as the chips are.
 *
 * "Edit Solution" flies the Solution Detail drawer out over the plan (the caller owns it)
 * rather than navigating to the dashboard: leaving the plan to change a Constraint and
 * then finding the way back was the loop this bar exists to close.
 *
 * No permission branch. Every role that can read a Resource Plan holds `viewSolutionDetail`
 * on the same Account, so there is no audience here that may see the plan but not its
 * Solution — see `loadSolutionContext` in src/api/resource-plans.ts.
 *
 * Solution STATUS and POD SIZE are deliberately NOT here. Both are real Solution
 * attributes and both were in the first cut; neither changes a pricing decision, and
 * dropping the two least-relevant fields is what fits the rest on one line at 1280px.
 * They are one click away on Solution Detail. Add a field back only if it would change
 * what a planner types into the grid.
 *
 * Since 2026-10-22 it is a collapsible "Setup" strip ("Say it once", docs/design-system.md
 * §9: inherited settings and classification tags collapse into one on-demand strip).
 * Collapsed — the default — is ONE row: CRM solution, Tier, Intensity, Expectation, Fit
 * Score, a "Constraints · N" toggle and the edit button. Expanded adds the Constraint chip
 * row and Industry. The choice persists per viewer in localStorage; the NOS Deal field
 * that used to sit here is gone — the status chip is the Deal's one home.
 */

import { ChevronDown, ChevronUp, PanelRightOpen } from "lucide-react";
import { useState } from "react";
import { FitBadge } from "@/components/shared/FitBadge";
import { expectationLevelDef } from "@/lib/expectation-levels";
import { cn } from "@/lib/utils";
import type { SolutionContextView } from "@/schemas/resource-plans";

/**
 * Broad to narrow — what we sell, what we sell it on, what it is about, who it is for —
 * so a Constraint profile reads the same wherever it appears.
 */
const GROUP_ORDER = ["Offering", "Platform", "Domain", "ICP"];

/**
 * One hue per Constraint group, label-style (Linear / GitHub / Notion): a soft tint, a
 * colored dot and darker text of the same hue. The hue says WHICH group a chip belongs to,
 * so a profile reads at a glance — violet, blue, green, orange. Red is left out because it
 * would read as an error. Tokens only — see .claude/rules/design-system.md.
 */
const GROUP_TONE: Record<string, { chip: string; dot: string }> = {
  Offering: {
    chip: "border-brand-500/25 bg-brand-500/10 text-brand-700 dark:text-brand-300",
    dot: "bg-brand-500",
  },
  Platform: {
    chip: "border-info-500/25 bg-info-500/10 text-info-700 dark:text-info-300",
    dot: "bg-info-500",
  },
  Domain: {
    chip: "border-success-500/25 bg-success-500/10 text-success-700 dark:text-success-300",
    dot: "bg-success-500",
  },
  ICP: {
    chip: "border-warning-500/25 bg-warning-500/10 text-warning-700 dark:text-warning-300",
    dot: "bg-warning-500",
  },
};
const OTHER_TONE = { chip: "border-border bg-muted text-foreground", dot: "bg-muted-foreground" };

function ConstraintChip(props: { name: string; groupName: string; inherited: boolean }) {
  const tone = GROUP_TONE[props.groupName] ?? OTHER_TONE;
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-md border px-2 text-xs font-medium",
        tone.chip,
        // Inherited from the Account: same hue, outlined only — set here is filled.
        props.inherited && "border-dashed bg-transparent",
      )}
      title={
        props.inherited
          ? `${props.name} — inherited from the Account when this Solution was created`
          : `${props.name} — added to this Solution specifically`
      }
    >
      <span aria-hidden className={cn("h-1.5 w-1.5 shrink-0 rounded-full", tone.dot)} />
      {props.name}
    </span>
  );
}

const SETUP_EXPANDED_KEY = "plan-setup:expanded";

/** Per-viewer convenience only — a blocked or empty store just means "collapsed". */
function readSetupExpandedPref(): boolean {
  try {
    return localStorage.getItem(SETUP_EXPANDED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSetupExpandedPref(expanded: boolean): void {
  try {
    localStorage.setItem(SETUP_EXPANDED_KEY, String(expanded));
  } catch {
    // Storage unavailable: the toggle still works for this visit.
  }
}

function groupRank(name: string): number {
  const i = GROUP_ORDER.indexOf(name);
  return i === -1 ? GROUP_ORDER.length : i;
}

function Field(props: { label: string; children: React.ReactNode; title?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{props.label}</span>
      <span className="truncate text-sm font-medium" title={props.title}>
        {props.children}
      </span>
    </div>
  );
}

/** An attribute the Solution did not set for itself, so the reader knows where it came from. */
function Inherited() {
  return (
    <span
      className="ml-1 text-xs font-normal text-muted-foreground"
      title="Not set on the Solution — inherited from the Account. Change it on the Account, or override it on the Solution."
    >
      {" "}
      inherited
    </span>
  );
}

export function PlanSolutionBar({
  context,
  onEditSolution,
}: {
  context: SolutionContextView | undefined;
  /** Opens the Solution Detail drawer over the plan. */
  onEditSolution: () => void;
}) {
  const [expanded, setExpanded] = useState(readSetupExpandedPref);
  if (context === undefined) return null;

  const constraintCount = context.constraints.length;

  const expectation = expectationLevelDef(context.expectationLevel);
  const byGroup = new Map<string, typeof context.constraints>();
  for (const c of context.constraints) {
    const list = byGroup.get(c.groupName) ?? [];
    list.push(c);
    byGroup.set(c.groupName, list);
  }
  const groups = [...byGroup.entries()].sort((a, b) => groupRank(a[0]) - groupRank(b[0]));

  return (
    <section
      aria-label="Setup — Solution context"
      className="rounded-md border border-border bg-card px-4 py-2.5"
    >
      <div className="flex min-w-0 flex-wrap items-start gap-x-6 gap-y-3">
        <Field label="Solution" title={context.solutionName}>
          {context.solutionName}
        </Field>
        <Field label="Tier">
          {context.tierName ?? <span className="text-muted-foreground">Not set</span>}
          {context.tierName !== null && context.tierInherited && <Inherited />}
        </Field>
        <Field
          label="Intensity"
          title="The Solution's points demand per month. Drives the Availability dimension of its Fit Score."
        >
          {context.intensity}
        </Field>
        <Field
          label="Expectation"
          title={
            expectation === undefined
              ? undefined
              : `Level ${context.expectationLevel} — ${expectation.label}`
          }
        >
          {context.expectationLevel}
          {expectation !== undefined && (
            <span className="ml-1 font-normal text-muted-foreground"> {expectation.label}</span>
          )}
          {context.expectationInherited && <Inherited />}
        </Field>
        <Field label="Fit Score">
          <FitBadge
            fit={context.fit}
            size="xs"
            title="The Solution's last calculated Fit Score, across its staffed Pod. Recalculated when its assignments change."
          />
        </Field>
        <button
          type="button"
          onClick={() => {
            const next = !expanded;
            setExpanded(next);
            writeSetupExpandedPref(next);
          }}
          aria-expanded={expanded}
          className="inline-flex h-7 shrink-0 items-center gap-1 self-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          title={
            expanded
              ? "Hide the Solution's Constraints and Industry"
              : "Show the Solution's Constraints and Industry"
          }
        >
          {constraintCount === 0 ? "No Constraints" : `Constraints · ${constraintCount}`}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        {/*
         * The button is the whole reason the bar can stay read-only: everything beside it
         * is a fact the planner needs and a control they should not have here.
         */}
        <button
          type="button"
          onClick={onEditSolution}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Edit Solution"
          title="Edit Solution — opens it beside the plan to change its Constraints, Expectation Level or Pod"
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
      </div>

      {expanded && groups.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border pt-2.5">
          <Field label="Industry">
            {context.industry ?? <span className="text-muted-foreground">Not set</span>}
          </Field>
          {groups.map(([groupName, items]) => (
            <div key={groupName} className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {groupName}
              </span>
              {items.map((c) => (
                <ConstraintChip
                  key={c.id}
                  name={c.name}
                  groupName={groupName}
                  inherited={c.inherited}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {expanded && groups.length === 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 border-t border-border pt-2.5">
          <Field label="Industry">
            {context.industry ?? <span className="text-muted-foreground">Not set</span>}
          </Field>
          <p className="text-xs text-muted-foreground">
            No Constraints on this Solution yet — Constraint Match is the largest part of a Fit
            Score, so an unconstrained Solution scores every candidate the same. Add them on the
            Solution.
          </p>
        </div>
      )}
    </section>
  );
}
